import { fetchPublicAPI, NetworkError } from "hooks/usePublicAPI";
import type { GrantMethod } from "pages/Login/login-grant-flow";

import {
  NO_REENTRY_OPTIONS,
  parseReentryOptions,
  parseStartedReentry,
  reentrySendFailure,
  type ReentryFailure,
  type ReentryOptions,
  type StartedReentry,
} from "./reentry";

export type ReentrySendAnswer =
  | ({ kind: "started" } & StartedReentry)
  | { kind: "failed"; failure: ReentryFailure };

function post(route: string, body: Record<string, unknown>): Promise<unknown> {
  return fetchPublicAPI<unknown>(route, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** What the server will send for the person this spent link was made for,
 * as masked displays. A link it does not recognise, or a failure to ask,
 * offers nothing: the screen then asks the visitor where to send it.
 * `report` hears of a failure that was not a plain refusal. */
export async function fetchReentryOptions(
  quoteId: string,
  token: string,
  report: (error: unknown) => void,
): Promise<ReentryOptions> {
  try {
    return parseReentryOptions(
      await post(`/quotes/${quoteId}/reentry/options`, { token }),
    );
  } catch (error) {
    const refused =
      error instanceof NetworkError &&
      (error.statusCode === 404 || error.statusCode === 429);
    if (!refused) report(error);
    return NO_REENTRY_OPTIONS;
  }
}

/** Asks for a code or link to a destination the server holds for the link's
 * recipient. Accepting the terms is what the button's disclosure says. */
export async function sendReentry(input: {
  quoteId: string;
  token: string;
  method: GrantMethod;
  options: ReentryOptions;
  /** Hears of a failure that was not a plain refusal. */
  report: (error: unknown) => void;
}): Promise<ReentrySendAnswer> {
  const { quoteId, token, method, options, report } = input;
  try {
    const raw = await post(`/quotes/${quoteId}/reentry/send`, {
      token,
      method,
      terms_accepted: true,
    });
    const started = parseStartedReentry(raw, quoteId, method, options);
    if (started) return { kind: "started", ...started };
    report(new Error("reentry send answered an unexpected shape"));
    return { kind: "failed", failure: "other" };
  } catch (error) {
    const failure = reentrySendFailure(
      error instanceof NetworkError ? error.statusCode : undefined,
    );
    if (failure === "other") report(error);
    return { kind: "failed", failure };
  }
}
